import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { projectsApi, usersApi } from '../../lib/api';
import DashboardLayout from '../../components/DashLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import { Checkbox } from '../../components/ui/checkbox';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  Plus,
  Search,
  Edit2,
  Trash2,
  Loader2,
  FolderKanban,
  Users,
  Calendar,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';

const ProjectsManagement = () => {
  const { isSuperAdmin } = useAuth();
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  const [showProjectDialog, setShowProjectDialog] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [projectForm, setProjectForm] = useState({
    name: '',
    description: '',
    member_ids: [],
  });
  const [formLoading, setFormLoading] = useState(false);

  const [deleteDialog, setDeleteDialog] = useState({ open: false, project: null });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [projectsRes, usersRes] = await Promise.all([
        projectsApi.list(),
        usersApi.list({}),
      ]);
      setProjects(projectsRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredProjects = projects.filter((project) =>
    project.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleCreateProject = async () => {
    if (!projectForm.name.trim()) return;

    setFormLoading(true);
    try {
      if (editingProject) {
        await projectsApi.update(editingProject.id, projectForm);
      } else {
        await projectsApi.create(projectForm);
      }
      await loadData();
      setShowProjectDialog(false);
      resetForm();
    } catch (error) {
      console.error('Failed to save project:', error);
      alert(error.response?.data?.detail || 'Failed to save project');
    } finally {
      setFormLoading(false);
    }
  };

  const handleEditProject = (project) => {
    setEditingProject(project);
    setProjectForm({
      name: project.name,
      description: project.description || '',
      member_ids: project.members?.map((m) => m.id) || [],
    });
    setShowProjectDialog(true);
  };

  const handleDeleteProject = async () => {
    if (!deleteDialog.project) return;

    try {
      await projectsApi.delete(deleteDialog.project.id);
      await loadData();
      setDeleteDialog({ open: false, project: null });
    } catch (error) {
      console.error('Failed to delete project:', error);
      alert(error.response?.data?.detail || 'Failed to delete project');
    }
  };

  const resetForm = () => {
    setEditingProject(null);
    setProjectForm({
      name: '',
      description: '',
      member_ids: [],
    });
  };

  const toggleMember = (userId) => {
    setProjectForm((prev) => ({
      ...prev,
      member_ids: prev.member_ids.includes(userId)
        ? prev.member_ids.filter((id) => id !== userId)
        : [...prev.member_ids, userId],
    }));
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="projects-management">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white tracking-tight flex items-center gap-2">
              <FolderKanban className="w-8 h-8 text-green-500" />
              Project Management
            </h1>
            <p className="text-muted-foreground mt-1">Create and manage projects</p>
          </div>
          <Button
            onClick={() => {
              resetForm();
              setShowProjectDialog(true);
            }}
            className="bg-primary hover:bg-primary/90"
            data-testid="create-project-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Project
          </Button>
        </div>

        {/* Search */}
        <Card className="bg-[#0A0A0B] border-[#27272A]">
          <CardContent className="pt-4">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search projects..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-[#121214] border-[#27272A]"
                data-testid="search-projects-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Projects Table */}
        <Card className="bg-[#0A0A0B] border-[#27272A]">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="border-[#27272A] hover:bg-transparent">
                  <TableHead className="text-muted-foreground">Project</TableHead>
                  <TableHead className="text-muted-foreground">Owner</TableHead>
                  <TableHead className="text-muted-foreground">Members</TableHead>
                  <TableHead className="text-muted-foreground">Created</TableHead>
                  <TableHead className="text-right text-muted-foreground">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredProjects.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                      No projects found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredProjects.map((project) => (
                    <TableRow
                      key={project.id}
                      className="border-[#27272A] hover:bg-white/5"
                      data-testid={`project-row-${project.id}`}
                    >
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                            <FolderKanban className="w-5 h-5 text-green-500" />
                          </div>
                          <div>
                            <span className="font-medium text-white">{project.name}</span>
                            {project.description && (
                              <p className="text-xs text-muted-foreground truncate max-w-[200px]">
                                {project.description}
                              </p>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">{project.owner_name}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Users className="w-4 h-4 text-muted-foreground" />
                          <span className="text-muted-foreground">{project.members?.length || 0}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground mono text-sm">
                        {format(parseISO(project.created_at), 'PP')}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEditProject(project)}
                            className="h-8 w-8 text-muted-foreground hover:text-white"
                            data-testid={`edit-project-${project.id}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setDeleteDialog({ open: true, project })}
                            className="h-8 w-8 text-muted-foreground hover:text-destructive"
                            data-testid={`delete-project-${project.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Create/Edit Project Dialog */}
        <Dialog open={showProjectDialog} onOpenChange={setShowProjectDialog}>
          <DialogContent className="bg-[#121214] border-[#27272A] sm:max-w-[520px]" data-testid="project-dialog">
            <DialogHeader>
              <DialogTitle className="text-white">
                {editingProject ? 'Edit Project' : 'Create New Project'}
              </DialogTitle>
              <DialogDescription>
                {editingProject ? 'Update project details below.' : 'Add a new project to the system.'}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="project-name">Project Name</Label>
                <Input
                  id="project-name"
                  value={projectForm.name}
                  onChange={(e) => setProjectForm({ ...projectForm, name: e.target.value })}
                  placeholder="My Awesome Project"
                  className="bg-[#0A0A0B] border-[#27272A]"
                  data-testid="project-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="project-description">Description (Optional)</Label>
                <Textarea
                  id="project-description"
                  value={projectForm.description}
                  onChange={(e) => setProjectForm({ ...projectForm, description: e.target.value })}
                  placeholder="Describe your project..."
                  className="bg-[#0A0A0B] border-[#27272A] min-h-[80px]"
                  data-testid="project-description-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Team Members</Label>
                <ScrollArea className="h-[200px] border border-[#27272A] rounded-lg p-3">
                  {users
                    .filter((u) => u.role === 'user')
                    .map((user) => (
                      <div
                        key={user.id}
                        className="flex items-center gap-3 py-2 hover:bg-white/5 px-2 rounded-md cursor-pointer"
                        onClick={() => toggleMember(user.id)}
                        data-testid={`member-checkbox-${user.id}`}
                      >
                        <Checkbox
                          checked={projectForm.member_ids.includes(user.id)}
                          onCheckedChange={() => toggleMember(user.id)}
                        />
                        <span className="text-white">{user.name}</span>
                        <span className="text-xs text-muted-foreground">{user.email}</span>
                      </div>
                    ))}
                  {users.filter((u) => u.role === 'user').length === 0 && (
                    <p className="text-muted-foreground text-sm text-center py-4">No users available</p>
                  )}
                </ScrollArea>
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowProjectDialog(false)}
                className="border-[#27272A]"
                data-testid="cancel-project-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateProject}
                disabled={formLoading}
                className="bg-primary"
                data-testid="save-project-btn"
              >
                {formLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : editingProject ? (
                  'Update Project'
                ) : (
                  'Create Project'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
          <AlertDialogContent className="bg-[#121214] border-[#27272A]" data-testid="delete-project-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-white">Delete Project</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete <strong>{deleteDialog.project?.name}</strong>? All tasks in this
                project will also be deleted.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="border-[#27272A]">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDeleteProject} className="bg-destructive hover:bg-destructive/90">
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
};

export default ProjectsManagement;
